<template>
    <div class="rounded-lgr p-5 border bg-white">
        <BaseTabs
            v-model="activeTab"
            :tab-ids="tabIds"
            :orientation="isMd ? 'vertical' : 'horizontal'"
            indicator-class="hidden"
            :list-class="isMd ? 'pr-5 border-r grow shrink-0 basis-70 gap-0 shadow-none' : 'gap-2'"
            trigger-class="rounded-md p-1.5 body-small data-[state=active]:text-black data-[state=active]:body-small-bold data-[state=active]:bg-valence-grey-100 transition-[colors] whitespace-nowrap flex-none">
            <template v-for="id in tabIds" :key="id" #[`trigger-${id}`]>
                <div class="flex items-center gap-2 font-scaffold">
                    <img :src="icon" alt="active element indicator" :class="['transition-[width,height]', activeTab === id ? 'size-4' : 'size-0']" />
                    {{ PERSPECTIVE_PAIR_LENS_ANCHOR[id] }}
                </div>
            </template>
            <template v-for="id in tabIds" :key="id" #[`content-${id}`]>
                <div class="fade-in pt-4 md:pt-0 md:pl-5 flex flex-col gap-6 min-h-60">
                    <h3 class="nadia-subtitle">{{ dimensionLensCardTitle }}</h3>
                    <DimensionBar
                        v-if="activeLens"
                        :code="dimension.dimension"
                        :binned-score="activeLens.binned_score"
                        :left-label="lensData.leftLabel"
                        :right-label="lensData.rightLabel" />
                    <MarkdownText class="body-small" :text="dimensionLensDescription" />
                </div>
            </template>
        </BaseTabs>
    </div>
</template>

<script setup lang="ts">
import { breakpointsTailwind, useBreakpoints } from "@vueuse/core";
import type { PerspectiveReportDimension } from "~vue/appTypes";
import BaseTabs from "~vue/components/BaseTabs.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { DIMENSION_LETTER_TO_LENS, PERSPECTIVE_LENS_CARD_DATA, PERSPECTIVE_PAIR_LENS_ANCHOR } from "~vue/pages/perspectiveTypes";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { calculateLensSummary, mapBinnedScoreToCardText } from "~vue/utils/perspectiveUtils";
import { computed, ref, watch } from "vue";

import DimensionBar from "./DimensionBar.vue";

const isMd = useBreakpoints(breakpointsTailwind).greaterOrEqual("md");

type DimensionCardProps = {
    dimension: PerspectiveReportDimension;
    icon: string;
};
const { dimension, icon } = defineProps<DimensionCardProps>();

const tabIds = DIMENSION_LETTER_TO_LENS[dimension.dimension];
const activeTab = ref(tabIds[0]);

watch(activeTab, (lens) => {
    // Normalize to lowercase snake_case per Amplitude naming conventions (#1): the canonical
    // PerspectiveLens keys are kebab-case ("expressive-contained") and dimension codes are uppercase ("EI").
    void logUserInteraction(AmplitudeEventNames.PRIVATE_PROFILE_TAB_CLICKED, {
        tab_type: "lens",
        tab: lens.replace(/-/g, "_"),
        dimension: dimension.dimension.toLowerCase(),
    });
});

const activeLens = computed(() => {
    return dimension.lenses.find((lens) => lens.lens === activeTab.value)!;
});

const lensData = computed(() => {
    return PERSPECTIVE_LENS_CARD_DATA[activeTab.value];
});

const dimensionLensCardTitle = computed(() => {
    return calculateLensSummary(activeLens.value, lensData.value.leftLabel, lensData.value.rightLabel);
});
const dimensionLensDescription = computed(() => {
    return mapBinnedScoreToCardText(activeTab.value, activeLens.value.binned_score);
});
</script>
